import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { Box, Typography } from '@mui/material';

mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  themeVariables: {
    nodeTextColor: '#222',
    arrowColor: '#eeeeee', // Thematic light gray (grey.200)
    lineColor: '#eeeeee',  // Also set line color for consistency
  },
});

let diagramCounter = 0;

interface MermaidDiagramProps {
  chart: string;
}

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const render = async () => {
      if (!containerRef.current) return;
      setError(null);

      try {
        const id = `mermaid-diagram-${++diagramCounter}`;
        const { svg } = await mermaid.render(id, chart.trim());
        if (!cancelled && containerRef.current) {
          containerRef.current.innerHTML = svg;

          // Mermaid miscalculates foreignObject height when labels contain HTML.
          // Use a small timeout so the browser fully lays out foreignObject content
          // before we measure scrollHeight.
          setTimeout(() => {
            if (!containerRef.current) return;

            containerRef.current.querySelectorAll('foreignObject').forEach((fo) => {
              const body = fo.querySelector('div');
              if (body) {
                const contentHeight = body.scrollHeight;
                const paddedHeight = contentHeight + 16;

                fo.setAttribute('height', String(paddedHeight));

                // Expand the parent node rect to match
                const g = fo.closest('.node');
                if (g) {
                  const rect = g.querySelector('rect');
                  if (rect) {
                    const current = parseFloat(rect.getAttribute('height') || '0');
                    if (paddedHeight + 8 > current) {
                      rect.setAttribute('height', String(paddedHeight + 8));
                    }
                  }
                }
              }
            });

            // Allow the SVG itself to grow beyond its calculated height
            const svgEl = containerRef.current.querySelector('svg');
            if (svgEl) {
              svgEl.style.overflow = 'visible';
              svgEl.removeAttribute('height'); // Let the viewBox + overflow handle sizing
            }
          }, 50);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to render diagram');
        }
      }
    };

    render();
    return () => {
      cancelled = true;
    };
  }, [chart]);

  if (error) {
    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'error.light',
          borderRadius: 1,
          p: 2,
          my: 1,
          bgcolor: 'error.50',
        }}
      >
        <Typography variant="caption" color="error" sx={{ fontFamily: 'monospace' }}>
          Mermaid error: {error}
        </Typography>
        <Box
          component="pre"
          sx={{ mt: 1, fontSize: '0.8rem', whiteSpace: 'pre-wrap', color: 'text.secondary' }}
        >
          {chart}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        my: 2,
        display: 'flex',
        justifyContent: 'center',
        '& svg': { maxWidth: '100%', height: 'auto', overflow: 'visible' },
        '& foreignObject': { overflow: 'visible' },
        // Override global white color that cascades into SVG foreignObject HTML content
        '& foreignObject *': { color: '#222' },
        '& foreignObject a': { color: '#32CA1D' },
      }}
    />
  );
};

export default MermaidDiagram;
